/**
 * One Todos row — the SINGLE rendering + completion implementation.
 *
 * Both surfaces use this component: the focused cross-org Todos page and the
 * contextual pane inside a Chat/Room. They differ only in the props below
 * (`density`, `showContext`, `onOpen`); everything a row *does* lives here on
 * purpose, so a fix to acknowledging or resolving lands on both at once.
 *
 * ── Completion goes through the endpoint that already owns each type ────────
 * Nothing here is reimplemented. `ack` → POST /messages/:id/acknowledge,
 * `poll` → the poll vote endpoints (via the existing PollCard), `reply_later`
 * → POST /reply-later/:id/resolve, `task` → POST /todos/tasks/:id/complete.
 * There is deliberately no "complete a todo" call, because there is no such
 * endpoint and inventing a client-side equivalent would fork the truth.
 *
 * ── Store writes follow the data layer's documented contract ────────────────
 * Only task writes broadcast `todos.update`. So:
 *   • ack / poll / reply_later → `removeItem()`, which drops the row from every
 *     view AND decrements the nav badge optimistically. Without that the badge
 *     says 3 over an empty list — the exact inbox failure this surface exists
 *     to avoid.
 *   • task → `applyTask()` / `removeTask()`, which re-file the row (an open
 *     view drops a completed task, a done view gains it) and leave the badge to
 *     the WS broadcast that is already coming.
 * A row is only ever retired when it was `open`; a row that is already `done`
 * renders its result read-only, so there is no "retire something twice" path.
 *
 * ── Ordering ───────────────────────────────────────────────────────────────
 * The feed is OLDEST-FIRST, so the useful timestamp is AGE, not clock time:
 * "12d" on the top row is the whole point of the surface. Due dates override
 * it, and overdue is called out.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TodoItem, TodoTask, Poll, AckUser } from '@goodchat/shared/types';
import {
  acknowledgeMessage,
  resolveReplyLater,
  deleteReplyLater,
  updateReplyLater,
  completeTask,
  reopenTask,
  deleteTask,
} from '@/lib/api';
import { useTodosStore } from '@/stores/todos.store';
import { getSmartSnoozePresets } from '@/lib/reply-later-presets';
import { PollCard } from '@/components/chat/poll-card';
import { toast } from '@/stores/toast.store';

export interface TodoRowProps {
  item: TodoItem;
  /**
   * `false` inside the per-room pane, where "in #Room · Org" repeats the
   * header on every row. Defaults to true (the cross-org focused view).
   */
  showContext?: boolean;
  /** 'compact' for the 384px room pane; 'comfortable' (default) for the page. */
  density?: 'comfortable' | 'compact';
  /** Highlighted (a split-view selection on the focused page). */
  isSelected?: boolean;
  /**
   * Open the thing this row is about. Omit and the row body is not clickable —
   * every action stays reachable either way.
   */
  onOpen?: (item: TodoItem) => void;
  /** Viewer id — a task shows Delete only to its creator (or an org admin). */
  currentUserId?: string;
  /** Viewer is owner/admin of `item.orgId`; also allowed to delete a task. */
  canAdminOrg?: boolean;
  /**
   * Start with the poll open. True in the detail pane, whose entire job is to
   * show what the list row had to cut — a "Vote" button there would just be a
   * second click to reach content there is already room for.
   */
  expandPoll?: boolean;
}

// ============================================================
// Formatting
// ============================================================

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact age: "now", "5m", "3h", "12d", "8w". */
export function formatAge(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < MINUTE) return 'now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`;
  const days = Math.floor(diff / DAY);
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

export interface DueLabel {
  text: string;
  /** Past its due date — the caller renders this in red. */
  overdue: boolean;
}

/** "Overdue 3d" / "Due today" / "Due tomorrow" / "Due Mar 4". */
export function formatDue(iso: string, now: number = Date.now()): DueLabel {
  const due = new Date(iso);
  const ts = due.getTime();
  if (Number.isNaN(ts)) return { text: '', overdue: false };

  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDue = new Date(ts);
  startOfDue.setHours(0, 0, 0, 0);
  const dayDelta = Math.round((startOfDue.getTime() - startOfToday.getTime()) / DAY);

  if (ts < now) {
    if (dayDelta === 0) return { text: 'Due today', overdue: true };
    const late = Math.abs(dayDelta);
    return { text: `Overdue ${late}d`, overdue: true };
  }
  if (dayDelta === 0) return { text: 'Due today', overdue: false };
  if (dayDelta === 1) return { text: 'Due tomorrow', overdue: false };
  return {
    text: `Due ${due.toLocaleDateString([], { month: 'short', day: 'numeric' })}`,
    overdue: false,
  };
}

/** "Snoozed until tomorrow 9:00 AM" — matches the inbox's phrasing. */
function formatSnooze(iso: string): string {
  const target = new Date(iso);
  const now = new Date();
  if (target.getTime() <= now.getTime()) return 'Snooze expired';
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (target.toDateString() === now.toDateString()) {
    return `Snoozed until ${target.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  if (target.toDateString() === tomorrow.toDateString()) {
    return `Snoozed until tomorrow ${target.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  return `Snoozed until ${target.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * Has voting finished this row off?
 *
 * A single-choice poll is done the moment you pick, which matches the server:
 * `pollConditions()` defines an open poll as one you have NOT voted in.
 *
 * A multi-select poll is NOT done on the first pick. Retiring it there tore the
 * row off the screen mid-vote and left the remaining options reachable only by
 * abandoning Todos for the room. It stays listed and stays counted — so the
 * badge still agrees with the list — until the next load applies the server's
 * view. Exported so this rule is pinned by a test; the DOM-less test env cannot
 * click a poll option.
 */
export function shouldRetireAfterVote(poll: Pick<Poll, 'myVotes' | 'allowMultiple'>): boolean {
  return poll.myVotes.length > 0 && !poll.allowMultiple;
}

/**
 * Every row leads with the same 20px circle so the list has one left edge.
 * A task's circle is its checkbox; the other three get a type glyph, which
 * also retires the "Task"/"Poll"/"Announcement" text chip that used to repeat
 * in words what the row already showed.
 */
const TYPE_GLYPH: Record<Exclude<TodoItem['type'], 'task'>, { path: string; tint: string }> = {
  ack: {
    path: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.6 20.6 0 01-1.44-3.685m4.102-.66a24.3 24.3 0 004.396 1.667c.28-1.464.428-2.976.428-4.522 0-1.546-.148-3.058-.428-4.522a24.3 24.3 0 00-4.396 1.667',
    tint: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  reply_later: {
    path: 'M3 8.688c0-.864.933-1.406 1.683-.977l7.108 4.061a1.125 1.125 0 010 1.954l-7.108 4.061A1.125 1.125 0 013 16.81V8.688zM12.75 8.688c0-.864.933-1.406 1.683-.977l7.108 4.061a1.125 1.125 0 010 1.954l-7.108 4.061a1.125 1.125 0 01-1.683-.977V8.688z',
    tint: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  },
  poll: {
    path: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
    tint: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
};

/**
 * Chips name the KIND of thing; the buttons name the action. An earlier pass
 * labelled the ack chip "Acknowledge", which sat inches from the Acknowledge
 * button and read like a second one.
 */
const TYPE_LABEL: Record<TodoItem['type'], string> = {
  ack: 'Announcement',
  reply_later: 'Reply later',
  poll: 'Poll',
  task: 'Task',
};

// ============================================================
// Row
// ============================================================

export function TodoRow({
  item,
  showContext = true,
  density = 'comfortable',
  isSelected = false,
  onOpen,
  currentUserId,
  canAdminOrg = false,
  expandPoll = false,
}: TodoRowProps) {
  const [busy, setBusy] = useState(false);
  const [pollOpen, setPollOpen] = useState(expandPoll);
  // Narrowed here rather than inline: `task` is derived from the item, not a
  // discriminant, so TS cannot rule out 'task' inside the JSX branch.
  const glyph = item.type === 'task' ? undefined : TYPE_GLYPH[item.type];
  const compact = density === 'compact';
  const isOpen = item.status === 'open';

  /**
   * Retire a row that has just left "open". Drops it from every view's list and
   * count, and decrements the nav badge — see the header note for why the three
   * view-backed types need the optimistic badge adjustment and tasks do not.
   */
  const retire = useCallback(() => {
    useTodosStore.getState().removeItem(item.id);
  }, [item.id]);

  const run = useCallback(
    async (fn: () => Promise<void>, failure: string) => {
      if (busy) return;
      setBusy(true);
      try {
        await fn();
      } catch {
        toast.error(failure);
      } finally {
        setBusy(false);
      }
    },
    [busy],
  );

  // ---- ack ----
  const handleAcknowledge = useCallback(() => {
    if (!item.messageId) return;
    void run(async () => {
      await acknowledgeMessage(item.messageId!);
      retire();
      toast.success('Acknowledged');
    }, 'Failed to acknowledge');
  }, [item.messageId, retire, run]);

  // ---- poll (PollCard owns the vote call; we only react to the result) ----
  const handlePollUpdate = useCallback(
    (poll: Poll) => {
      if (isOpen && shouldRetireAfterVote(poll)) retire();
      else useTodosStore.getState().updateItem(item.id, { poll });
    },
    [isOpen, item.id, retire],
  );

  // ---- reply later ----
  const replyLaterId = item.replyLater?.id;
  const handleResolve = useCallback(() => {
    if (!replyLaterId) return;
    void run(async () => {
      await resolveReplyLater(replyLaterId);
      retire();
      toast.success('Resolved');
    }, 'Failed to resolve');
  }, [replyLaterId, retire, run]);

  const handleRemoveFlag = useCallback(() => {
    if (!replyLaterId) return;
    void run(async () => {
      await deleteReplyLater(replyLaterId);
      retire();
      toast.success('Removed');
    }, 'Failed to remove');
  }, [replyLaterId, retire, run]);

  const handleSnooze = useCallback(
    (snoozedUntil: string) => {
      if (!replyLaterId) return;
      void run(async () => {
        await updateReplyLater(replyLaterId, { snoozedUntil });
        // A snoozed row is not open — the server hides it until it comes due,
        // and so must the list and the badge.
        retire();
        toast.success(formatSnooze(snoozedUntil));
      }, 'Failed to snooze');
    },
    [replyLaterId, retire, run],
  );

  // ---- task ----
  const task = item.task ?? null;
  const applyTaskResult = useCallback(
    (next: TodoTask | null) => {
      if (!next) return;
      useTodosStore
        .getState()
        .applyTask(next, { name: item.orgName, color: item.orgColor });
    },
    [item.orgColor, item.orgName],
  );

  const handleToggleTask = useCallback(() => {
    if (!task) return;
    void run(async () => {
      const res = task.completedAt ? await reopenTask(task.id) : await completeTask(task.id);
      applyTaskResult(res.task);
    }, task.completedAt ? 'Failed to reopen task' : 'Failed to complete task');
  }, [applyTaskResult, run, task]);

  const handleDeleteTask = useCallback(() => {
    if (!task) return;
    void run(async () => {
      await deleteTask(task.id);
      useTodosStore.getState().removeTask(task.id);
      toast.success('Task deleted');
    }, 'Failed to delete task');
  }, [run, task]);

  const canDeleteTask =
    !!task && (canAdminOrg || (!!currentUserId && task.createdBy.userId === currentUserId));

  // ---- labels ----
  // Lead with WHAT you owe, not who it came from. Rows used to headline the
  // author's name with the actual content demoted to a sub-line, so a list of
  // things to do read as a list of people — and three rows from the same
  // person were indistinguishable at a glance.
  const primary = task
    ? task.title
    : (item.poll?.question ?? item.contentPreview ?? item.authorDisplayName ?? 'Unknown');
  const age = useMemo(() => formatAge(item.sortAt), [item.sortAt]);

  /**
   * The date chip, or nothing.
   *
   * A reply-later's `dueAt` is its `snoozedUntil`, and a LAPSED snooze is
   * precisely how a flagged reply comes back to you — the server re-opens it
   * once `snoozed_until <= now()`. Running that through the generic due
   * formatter stamped "Snooze expired" in overdue red across the most ordinary
   * reply-later row there is. A snooze is only worth saying while it is still
   * in the future; once it lapses the row's presence in the list IS the signal.
   */
  const chip = useMemo<DueLabel | null>(() => {
    if (!item.dueAt) return null;
    if (item.type !== 'reply_later') return formatDue(item.dueAt);
    return Date.parse(item.dueAt) > Date.now()
      ? { text: formatSnooze(item.dueAt), overdue: false }
      : null;
  }, [item.dueAt, item.type]);

  const avatarSize = compact ? 'h-7 w-7' : 'h-9 w-9';
  const textSize = compact ? 'text-xs' : 'text-sm';

  return (
    <div
      /* A card, not a divider-separated line. Discrete rows with their own
         edges are most of why a list reads as "a set of things to do" rather
         than a feed — and it gives selection something to land on. */
      className={`group relative flex w-full items-start rounded-xl border transition-colors ${
        compact ? 'gap-2 py-2.5 pl-2 pr-2.5' : 'gap-2.5 py-2.5 pl-2.5 pr-3 sm:gap-3 sm:py-3 sm:pl-3 sm:pr-3.5'
      } ${
        isSelected
          ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40 dark:hover:border-gray-700 dark:hover:bg-gray-900'
      } ${onOpen ? 'cursor-pointer' : ''} ${busy ? 'opacity-60' : ''}`}
      data-todo-type={item.type}
      /* The whole card selects. In a split view, a row where only the title is
         clickable makes the pane feel broken — you click the obvious target
         (the row) and nothing happens. Interactive children stopPropagation. */
      onClick={onOpen ? () => onOpen(item) : undefined}
    >
      {/* Leading slot: a checkbox for tasks (the thing you tick), the author
          avatar for the three message-backed types (the thing you recognise). */}
      {task ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); void handleToggleTask(); }}
          disabled={busy}
          aria-pressed={!!task.completedAt}
          aria-label={task.completedAt ? `Reopen ${task.title}` : `Complete ${task.title}`}
          /*
            The visible circle stays 18-20px, but WCAG 2.2 SC 2.5.8 wants a 24px
            target and this is the control the whole surface exists for. Padding
            would shrink the circle (Tailwind sets box-sizing: border-box), so
            the hit area is extended with an inset pseudo-element instead — 36px
            to aim at, zero layout change.
          */
          className={`relative mt-0.5 flex flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors after:absolute after:-inset-2 after:content-[''] disabled:opacity-50 ${
            compact ? 'h-[18px] w-[18px]' : 'h-5 w-5'
          } ${
            task.completedAt
              ? 'border-green-500 bg-green-500 text-white'
              : 'border-gray-300 text-transparent hover:border-green-500 hover:text-green-500 dark:border-gray-600'
          }`}
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </button>
      ) : (
        <div
          aria-hidden
          className={`mt-0.5 flex flex-shrink-0 items-center justify-center rounded-full ${
            compact ? 'h-[18px] w-[18px]' : 'h-5 w-5'
          } ${glyph?.tint ?? ''}`}
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={glyph?.path} />
          </svg>
        </div>
      )}

      <div className="min-w-0 flex-1">
        {/* Row 1 — primary label + age */}
        <div className="flex items-baseline justify-between gap-2">
          {/* Not a button: the whole card is the click target now, and nesting
              one inside it fired selection twice. */}
          <span
            className={`min-w-0 truncate font-semibold text-gray-900 dark:text-gray-100 ${textSize} ${
              task?.completedAt ? 'line-through opacity-60' : ''
            }`}
          >
            {primary}
          </span>
          <span className="flex-shrink-0 text-[11px] text-gray-500 dark:text-gray-400" title={new Date(item.sortAt).toLocaleString()}>
            {age}
          </span>
        </div>

        {/* Row 2 — one muted provenance line: who, where, which org. Author
            moved here when the title started carrying the content. */}
        {(item.authorDisplayName || (showContext && (item.areaName || item.orgName))) && (
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
            {!task && item.authorDisplayName && (
              <span className="truncate font-medium">{item.authorDisplayName}</span>
            )}
            {showContext && item.areaName && (
              <span className="truncate">
                {!task && item.authorDisplayName ? '· ' : ''}#{item.areaName}
              </span>
            )}
            {showContext && item.orgName && (
              <>
                <span>·</span>
                {item.orgColor && (
                  <span
                    className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: item.orgColor }}
                  />
                )}
                <span className="truncate">{item.orgName}</span>
              </>
            )}
          </div>
        )}

        {/* Row 3 — the body. A task shows its notes; everything else the message
            preview (its primary label was the author).
            A poll is the exception: its `contentPreview` IS the question, which
            the PollCard below already leads with, so printing it here rendered
            the same sentence twice in a row. */}
        {task && task.notes && (
          <p className={`mt-1 line-clamp-2 text-gray-600 dark:text-gray-300 ${compact ? 'text-[11px]' : 'text-xs'} ${
            task.completedAt ? 'line-through opacity-60' : ''
          }`}>
            {task.notes}
          </p>
        )}

        {/* Row 4 — chips: what kind of thing this is, and when it is due */}
        {/* Chips carry only what the row cannot already show. The type chip is
            gone — the leading glyph says the same thing without a word — and
            "Anyone" is gone too: an unassigned task showing a chip that says
            nothing is just noise on every single row. */}
        <div className="mt-1 flex flex-wrap items-center gap-1.5 empty:mt-0">
          {chip && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                chip.overdue
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
              }`}
            >
              {chip.text}
            </span>
          )}
          {task?.assignee && (
            <span className="truncate rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {task.assignee.displayName}
            </span>
          )}
          {task && !task.isInternal && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              Guests can see this
            </span>
          )}
          {item.threadTitle && (
            <span className="truncate rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {item.threadTitle}
            </span>
          )}
        </div>

        {/* Type-specific block */}
        {item.type === 'ack' && (
          <AckBlock item={item} busy={busy} onAcknowledge={handleAcknowledge} compact={compact} />
        )}

        {/* A poll used to render its whole PollCard inline, which made one row
            four times the height of its neighbours and destroyed the list's
            rhythm — and it repeated the question, since the question is now the
            row's title. Collapsed to a one-line summary that opens on demand. */}
        {item.type === 'poll' && item.poll && item.messageId && (
          pollOpen ? (
            <div className="mt-1.5">
              <PollCard
                messageId={item.messageId}
                poll={item.poll}
                /* Closing a poll belongs in the conversation, not the todo list. */
                isAuthor={false}
                onPollUpdate={handlePollUpdate}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPollOpen(true); }}
              className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-700 dark:text-gray-300 dark:hover:border-blue-700 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
            >
              Vote
              <span className="font-normal text-gray-400 dark:text-gray-500">
                {item.poll.options.length} option{item.poll.options.length === 1 ? '' : 's'}
              </span>
            </button>
          )
        )}

        {item.type === 'reply_later' && item.replyLater && (
          <ReplyLaterActions
            busy={busy}
            onSnooze={handleSnooze}
            onResolve={handleResolve}
            onRemove={handleRemoveFlag}
          />
        )}

        {task && canDeleteTask && (
          <div className="mt-1.5">
            <button
              type="button"
              onClick={handleDeleteTask}
              disabled={busy}
              /*
                Two problems on a phone, not one. `opacity-0` until
                `group-hover` means a touchscreen — which has no hover — could
                only reach this by tabbing to it, so Delete was effectively
                invisible on mobile. And `py-0.5` made it a 23px target, just
                under WCAG 2.2 SC 2.5.8's 24px. It is now always visible below
                lg (where a coarse pointer is likely) and stays hover-revealed
                on the desktop layouts the treatment was designed for.
              */
              className="rounded-md border border-red-300 px-2 py-1 text-[11px] font-medium text-red-500 transition-opacity hover:bg-red-50 disabled:opacity-50 lg:opacity-0 lg:focus:opacity-100 lg:group-hover:opacity-100 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Ack block — progress + who's done / who's waiting
// ============================================================

function AckBlock({
  item,
  busy,
  compact,
  onAcknowledge,
}: {
  item: TodoItem;
  busy: boolean;
  compact: boolean;
  onAcknowledge: () => void;
}) {
  const summary = item.acknowledgmentSummary;
  const pct =
    summary && summary.total > 0 ? Math.round((summary.acknowledged / summary.total) * 100) : 0;

  return (
    <div className="mt-1.5">
      {summary && (
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] text-gray-500 dark:text-gray-400">
            {summary.acknowledged}/{summary.total}
          </span>
        </div>
      )}

      {!compact && summary && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {summary.acknowledgedUsers && summary.acknowledgedUsers.length > 0 && (
            <AvatarStack users={summary.acknowledgedUsers} total={summary.acknowledged} label="Done" />
          )}
          {summary.pendingUsers && summary.pendingUsers.length > 0 && (
            <AvatarStack
              users={summary.pendingUsers}
              total={summary.total - summary.acknowledged}
              label="Waiting"
            />
          )}
        </div>
      )}

      <div className="mt-1.5">
        {item.status === 'open' ? (
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={busy}
            className="rounded-md border border-green-300 px-2 py-0.5 text-[11px] font-medium text-green-600 transition-colors hover:bg-green-50 active:bg-green-100 disabled:opacity-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/30"
          >
            Acknowledge
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-400">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Acknowledged
          </span>
        )}
      </div>
    </div>
  );
}

function AvatarStack({ users, total, label }: { users: AckUser[]; total: number; label: string }) {
  if (users.length === 0 && total === 0) return null;
  const remaining = total - users.length;
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] text-gray-500 dark:text-gray-400">{label}:</span>
      <div className="flex -space-x-1.5">
        {users.map((u) => (
          <div
            key={u.userId}
            className="flex h-4 w-4 items-center justify-center rounded-full border border-white bg-gray-200 text-[7px] font-medium text-gray-600 dark:border-gray-800 dark:bg-gray-700 dark:text-gray-300"
            title={u.displayName}
          >
            {u.profilePictureUrl ? (
              <img src={u.profilePictureUrl} alt="" className="h-4 w-4 rounded-full object-cover" />
            ) : (
              u.displayName.charAt(0).toUpperCase()
            )}
          </div>
        ))}
      </div>
      {remaining > 0 && <span className="text-[10px] text-gray-400">+{remaining}</span>}
    </div>
  );
}

// ============================================================
// Reply Later actions
// ============================================================

function ReplyLaterActions({
  busy,
  onSnooze,
  onResolve,
  onRemove,
}: {
  busy: boolean;
  onSnooze: (iso: string) => void;
  onResolve: () => void;
  onRemove: () => void;
}) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <div className="relative">
        <button
          type="button"
          onClick={() => setSnoozeOpen((v) => !v)}
          disabled={busy}
          className="rounded-md border border-amber-300 px-2 py-0.5 text-[11px] font-medium text-amber-600 transition-colors hover:bg-amber-50 disabled:opacity-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30"
        >
          Snooze
        </button>
        {snoozeOpen && (
          <SnoozeMenu
            onSnooze={(iso) => {
              setSnoozeOpen(false);
              onSnooze(iso);
            }}
            onClose={() => setSnoozeOpen(false)}
          />
        )}
      </div>
      <button
        type="button"
        onClick={onResolve}
        disabled={busy}
        className="rounded-md border border-green-300 px-2 py-0.5 text-[11px] font-medium text-green-600 transition-colors hover:bg-green-50 disabled:opacity-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/30"
      >
        Resolve
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        className="rounded-md border border-red-300 px-2 py-0.5 text-[11px] font-medium text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
      >
        Remove
      </button>
    </div>
  );
}

/**
 * Snooze presets come from `@/lib/reply-later-presets`, the same module the
 * inbox uses — the *behaviour* (including the user's customised morning /
 * afternoon / evening hours) stays single-sourced even though this popover's
 * chrome is its own.
 */
function SnoozeMenu({ onSnooze, onClose }: { onSnooze: (iso: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const presets = getSmartSnoozePresets();

  return (
    <div
      ref={ref}
      className="absolute bottom-6 left-0 z-50 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="px-3 py-1 text-[10px] font-semibold uppercase text-gray-400">Snooze until</div>
      {presets.map((preset) => (
        <button
          key={preset.label}
          type="button"
          onClick={() => onSnooze(preset.getDate().toISOString())}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <svg className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {preset.label}
        </button>
      ))}
      <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
      {!showCustom ? (
        <button
          type="button"
          onClick={() => setShowCustom(true)}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <svg className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Custom...
        </button>
      ) : (
        <div className="space-y-1.5 px-3 py-1.5">
          <input
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
          />
          <input
            type="time"
            value={customTime}
            onChange={(e) => setCustomTime(e.target.value)}
            className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
          />
          <button
            type="button"
            disabled={!customDate}
            onClick={() => {
              const when = new Date(`${customDate}T${customTime || '09:00'}:00`);
              if (when.getTime() > Date.now()) onSnooze(when.toISOString());
            }}
            className="w-full rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            Set snooze
          </button>
        </div>
      )}
    </div>
  );
}
